import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  TrendingUp,
  LogOut,
  Moon,
  ArrowLeft,
  ChevronDown,
  SquarePen,
} from "lucide-react";
import { cn } from "../../../utils/function";
import { useTheme } from "../../../context/theme-context";
import useRequest from "../../hook/use-req";
import { UserResponse } from "../../types";
import SuiteSwitcher from "../suite-switcher";
import { getStoredOrgRole } from "../../../utils/org-role";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path?: string;
  badge?: number;
  children?: NavItem[];
}

// Convo CXO — its own sidebar/area, same nested-nav pattern as
// Unify/Automate's sidebars. Per the updated CXO dashboard design (Figma:
// ConvoHub-Dev-Handoff, node 5201-3790) the nav is trimmed down to just
// these items — the earlier Action board/KPIs & OKRs/Team performance/
// Reports/Access & roles placeholders aren't part of the new design, so
// they're dropped from the nav (their ComingSoonPage routes are left in
// main-routes.tsx in case anything still links to them directly). One
// sidebar for the whole CXO area — per direct ask, Campaigns sits alongside
// Insights Overview and Ads Creative Performance rather than swapping in its
// own separate contextual nav.
const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Insights Overview", icon: LayoutGrid, path: "/cxo/overview" },
  { id: "campaigns", label: "Campaigns", icon: SquarePen, path: "/cxo/campaigns" },
  { id: "ads-creative-performance", label: "Ads Creative Performance", icon: TrendingUp, path: "/cxo/ads-creative-performance" },
];

const CxoSidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark: darkMode, toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [user, setUser] = useState<UserResponse | null>(null);
  const role = user?.payload?.role || localStorage.getItem("role") || "";
  const orgRole = user?.payload?.org_role || getStoredOrgRole();

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    // "Campaigns" itself stays highlighted on every drill-down sub-route
    // (a campaign/ad set/ad's own detail page), not just the exact list path.
    if (path === "/cxo/campaigns") return location.pathname === path || /^\/cxo\/campaigns\/\d/.test(location.pathname);
    return location.pathname === path;
  };
  const isBranchActive = (item: NavItem): boolean => isActive(item.path) || (item.children?.some(isBranchActive) ?? false);

  const toggleExpanded = (id: string) => {
    if (!open) {
      onToggle();
      setTimeout(() => setExpanded((prev) => (prev.includes(id) ? prev : [...prev, id])), 300);
      return;
    }
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const renderItem = (item: NavItem, level = 0): React.ReactNode => {
    const Icon = item.icon;
    const hasChildren = !!item.children?.length;
    const isExpanded = expanded.includes(item.id);
    const active = isBranchActive(item);
    const iconSize = level === 0 ? "w-5 h-5" : "w-4 h-4";

    const row = (
      <div
        onClick={hasChildren ? () => toggleExpanded(item.id) : undefined}
        className={cn(
          `flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer select-none`,
          open ? "gap-3.5 justify-start" : "justify-center",
          active
            ? level === 0
              ? "text-gray-900 bg-amber-400"
              : "text-amber-400"
            : "text-gray-300 hover:text-white hover:bg-white/10"
        )}
      >
        <Icon className={cn(iconSize, "shrink-0", active ? "text-gray-900" : "text-gray-400")} />
        {open &&
          (hasChildren ? (
            <div className="flex items-center justify-between flex-1">
              <span className="tracking-tight">{item.label}</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isExpanded ? "rotate-180" : "")} />
            </div>
          ) : (
            <div className="flex items-center justify-between flex-1">
              <span className="tracking-tight">{item.label}</span>
              {!!item.badge && (
                <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-[11px] font-semibold text-white leading-none">
                  {item.badge}
                </span>
              )}
            </div>
          ))}
        {!open && !!item.badge && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
        )}
      </div>
    );

    return (
      <div key={item.id} className="relative block">
        {hasChildren ? row : <Link to={item.path || "#"} className="block">{row}</Link>}
        {hasChildren && open && isExpanded && (
          <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
            {item.children!.map((child) => renderItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      // Fixed dark rail per the reference design — deliberately not tied to
      // the app's own light/dark mode toggle (which still controls the main
      // content area below), same way the brand-yellow top bar is a fixed
      // CXO-specific choice rather than a new theme mode.
      className={`flex flex-col h-full m-3 rounded-lg bg-gray-900 transition-all duration-300 ${open ? "w-[257px]" : "w-[64px]"
        }`}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        {open ? (
          <div className="flex rounded-lg items-center gap-1.5">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="cxo" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="ml-auto flex items-center justify-center w-7 h-7 rounded-md border border-white/15 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              onClick={onToggle}
              title="Collapse"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 w-7">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="cxo" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/15 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              onClick={onToggle}
              title="Expand"
            >
              <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            </button>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => renderItem(item))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10 space-y-0.5">
        <div
          className={cn(
            "flex items-center px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors",
            open ? "justify-between" : "justify-center"
          )}
        >
          <div className="flex items-center gap-3.5">
            <Moon className="w-[18px] h-[18px] text-gray-400 shrink-0" strokeWidth={1.75} />
            {open && <span className="text-sm font-semibold text-gray-300 tracking-tight">Dark Mode</span>}
          </div>
          {open && (
            <button
              onClick={toggleTheme}
              className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0",
                darkMode ? "bg-blue-500" : "bg-gray-700"
              )}
              aria-label="Toggle dark mode"
            >
              <span
                className={cn(
                  "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200",
                  darkMode ? "translate-x-[18px]" : "translate-x-1"
                )}
              />
            </button>
          )}
        </div>

        <button
          className="flex w-full items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
          onClick={handleSignOut}
        >
          <LogOut className="w-[18px] h-[18px] text-gray-400 shrink-0" strokeWidth={1.75} />
          {open && <span className="tracking-tight">Log Out</span>}
        </button>
      </div>
    </div>
  );
};

export default CxoSidebar;
